import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { fmtInt } from "@/lib/format";

type NextTurnButtonProps = {
  month: number;
  /** Time-limited scenarios pass a number; open-ended → omit for ∞. */
  totalMonths?: number | undefined;
  cash: number;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
  /** Hard-disable the CTA (e.g. when the game is over). */
  disabled?: boolean;
  /** Short reason shown inside the button when disabled. */
  disabledLabel?: string | undefined;
};

/**
 * The primary game action — advances the player by one month.
 * Sits at the bottom of the sidebar with a live counter of month / cash.
 */
export function NextTurnButton({
  month,
  totalMonths,
  cash,
  loading = false,
  onClick,
  className,
  disabled = false,
  disabledLabel,
}: NextTurnButtonProps) {
  const inactive = disabled || loading;
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <button
        type="button"
        onClick={onClick}
        disabled={inactive}
        className={cn(
          "group relative w-full overflow-hidden rounded-xl px-4 py-3.5",
          disabled
            ? "bg-surface-raised text-ink-muted border border-stroke-strong"
            : "bg-gradient-to-b from-amber to-coral text-[#10141d]",
          "font-serif text-[15px] font-bold tracking-tight",
          !disabled &&
            "shadow-[0_10px_28px_-10px_rgba(245,178,99,0.6),inset_0_1px_0_rgba(255,255,255,0.25)]",
          "transition-transform active:scale-[0.98]",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        )}
      >
        {/* Shimmer */}
        <span
          className={cn(
            "pointer-events-none absolute inset-0",
            "bg-[linear-gradient(120deg,transparent_40%,rgba(255,255,255,0.2)_50%,transparent_60%)]",
            "-translate-x-full transition-transform duration-[600ms]",
            "group-hover:translate-x-full",
          )}
        />
        <span className="relative flex items-center justify-between gap-2">
          <span>
            {loading
              ? "Zpracovávám…"
              : disabled
                ? disabledLabel
                  ? `Konec · ${disabledLabel}`
                  : "Hra skončila"
                : "Další měsíc"}
          </span>
          <ArrowRight size={18} className="mono" strokeWidth={2.5} />
        </span>
      </button>
      <div className="flex items-center justify-between px-1 text-[11px] tracking-[0.05em] text-ink-dim">
        <span>
          Měsíc{" "}
          <b className="mono font-semibold text-ink">
            {totalMonths != null ? `${month} / ${totalMonths}` : month}
          </b>
        </span>
        <span>
          Kapitál <b className="mono font-semibold text-ink">{fmtInt(cash)} Kč</b>
        </span>
      </div>
      <div className="mono px-1 text-[10px] text-ink-dim">
        Tip: stisk <kbd className="rounded border border-stroke px-1 py-[0.5px] text-[9px]">N</kbd> posune tah
      </div>
    </div>
  );
}
